import { ApiError } from '../../api/client';
import type { ReportLogo } from './reportModel';

/**
 * Firmenlogo für PDF-Exporte (PHQ-92), pro Mandant, gespeichert in der pm-api
 * unter `/api/v1/settings/logo` (GET: jeder angemeldete Benutzer, PUT/DELETE: Recht
 * `admin.settings`). Der Mandant kommt wie bei allen API-Aufrufen über den Header
 * `X-Tenant-Id` (localStorage `pm_tenant`), nicht über den Pfad.
 *
 * Das frühere Browser-Logo (localStorage `phq.pdfLogo.<tenantId>`) wird nicht mehr
 * angezeigt; {@link readLegacyLogo} dient nur der einmaligen Übernahme.
 */

const LOGO_PATH = '/api/v1/settings/logo';

/** Obergrenze der Originaldatei; entspricht dem Server-Limit (524288 Byte). */
export const MAX_LOGO_BYTES = 512 * 1024;
export const LOGO_MIME_TYPES = ['image/png', 'image/jpeg'];
/** Recht, das PUT/DELETE auf dem Server verlangt. */
export const LOGO_PERMISSION = 'admin.settings';

const legacyKey = (tenantId: string) => `phq.pdfLogo.${tenantId}`;

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem('pm_token');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const tenantId = localStorage.getItem('pm_tenant');
  if (tenantId) headers['X-Tenant-Id'] = tenantId;
  return headers;
}

const STATUS_FALLBACK: Record<number, string> = {
  400: 'Die Datei wurde vom Server abgelehnt (nur PNG oder JPEG).',
  403: 'Dafür fehlt die Berechtigung (admin.settings).',
  413: `Die Datei ist zu groß (maximal ${Math.round(MAX_LOGO_BYTES / 1024)} KB).`,
};

/** Wirft einen ApiError mit der Meldung des Servers (Fallback je Statuscode). */
async function throwApiError(resp: Response): Promise<never> {
  const body = (await resp.json().catch(() => ({}))) as { error?: { code?: string; message?: string } };
  throw new ApiError(
    body.error?.code ?? 'UNKNOWN',
    body.error?.message || STATUS_FALLBACK[resp.status] || `HTTP ${resp.status}`,
    resp.status,
  );
}

/** Prüft Typ und Größe vor dem Upload. Wirft `Error('type'|'size')`. */
export function validateLogoFile(file: File): void {
  if (!LOGO_MIME_TYPES.includes(file.type)) throw new Error('type');
  if (file.size > MAX_LOGO_BYTES) throw new Error('size');
}

/** Wandelt Bild-Bytes in ein Logo (Data-URL + Pixelmaße). Wirft bei ungültiger Datei. */
export function blobToLogo(blob: Blob): Promise<ReportLogo> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read'));
    reader.onload = () => {
      const dataUrl = String(reader.result);
      const img = new Image();
      img.onerror = () => reject(new Error('read'));
      img.onload = () => resolve({ dataUrl, width: img.naturalWidth, height: img.naturalHeight });
      img.src = dataUrl;
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * Lädt das Logo des aktuellen Mandanten. 404 = kein Logo (null). Andere Fehler
 * werfen — der PDF-Export fängt sie ab und exportiert ohne Logo.
 * Der Browser-Cache übernimmt ETag/If-None-Match (`no-cache` des Servers).
 */
export async function fetchCompanyLogo(): Promise<ReportLogo | null> {
  const resp = await fetch(LOGO_PATH, { method: 'GET', headers: authHeaders() });
  if (resp.status === 404) return null;
  if (!resp.ok) await throwApiError(resp);
  return blobToLogo(await resp.blob());
}

/** Lädt das Logo per PUT (multipart `file`) hoch. */
export async function uploadCompanyLogo(file: Blob, filename = 'logo'): Promise<void> {
  const form = new FormData();
  form.append('file', file, filename);
  const resp = await fetch(LOGO_PATH, { method: 'PUT', headers: authHeaders(), body: form });
  if (!resp.ok) await throwApiError(resp);
}

export async function deleteCompanyLogo(): Promise<void> {
  const resp = await fetch(LOGO_PATH, { method: 'DELETE', headers: authHeaders() });
  if (!resp.ok) await throwApiError(resp);
}

/**
 * Altbestand aus dem Browser (vor der Server-Speicherung). Liefert die Bytes als
 * Blob oder null, wenn nichts oder nichts Lesbares hinterlegt ist.
 */
export function readLegacyLogo(tenantId: string | undefined): Blob | null {
  if (!tenantId) return null;
  try {
    const raw = localStorage.getItem(legacyKey(tenantId));
    if (!raw) return null;
    const l = JSON.parse(raw) as ReportLogo;
    const m = typeof l.dataUrl === 'string' ? /^data:(image\/(?:png|jpeg));base64,(.+)$/.exec(l.dataUrl) : null;
    if (!m) return null;
    const bin = atob(m[2]);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: m[1] });
  } catch { return null; }
}

export function clearLegacyLogo(tenantId: string | undefined): void {
  if (!tenantId) return;
  try { localStorage.removeItem(legacyKey(tenantId)); } catch { /* best-effort */ }
}
